<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useFacebookPageConnect } from 'dashboard/composables/useFacebookPageConnect';
import {
  facebookLoginScopeList,
  facebookScopeFlagsForAccount,
} from 'dashboard/helper/facebookScopes';
import FBChannel from 'dashboard/api/channel/fbChannel';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t, te } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const { preloadSdk, reauthorize } = useFacebookPageConnect();

// null means "not checked yet or the check failed" — distinct from an empty list,
// which would mean facebook granted nothing.
const grantedScopes = ref(null);
const isReconnecting = ref(false);

const requestedScopes = computed(() =>
  facebookLoginScopeList({
    includeInstagramScopes: Boolean(props.inbox.instagram_id),
    ...facebookScopeFlagsForAccount(
      isFeatureEnabledOnAccount.value,
      accountId.value
    ),
  })
);

const missingScopes = computed(() => {
  if (grantedScopes.value === null) return [];
  return requestedScopes.value.filter(
    scope => !grantedScopes.value.includes(scope)
  );
});

// Only the comment/publishing scopes have a friendly label. Everything else
// (base page scopes, instagram scopes) falls back to the raw facebook
// permission name below — that's also what Meta's own consent dialog shows,
// so it stays recognisable instead of leaking a translation key.
const permissionLabel = scope => {
  const key = `INBOX_MGMT.FACEBOOK_CONNECTION.PERMISSION_LABELS.${scope}`;
  return te(key) ? t(key) : scope;
};

const missingPermissionsText = computed(() =>
  missingScopes.value.map(permissionLabel).join(', ')
);

const fetchPermissions = async () => {
  try {
    const response = await FBChannel.fetchPagePermissions({
      inboxId: props.inbox.id,
    });
    grantedScopes.value = (response.data.permissions || [])
      .filter(entry => entry.status === 'granted')
      .map(entry => entry.permission);
  } catch (error) {
    grantedScopes.value = null;
  }
};

// A falsy result means the user closed the Facebook dialog or declined — a
// deliberate choice, so it passes silently. Only a genuine failure alerts.
const handleReconnect = async () => {
  isReconnecting.value = true;
  try {
    // Must request exactly the scopes `requestedScopes` diffs against above,
    // or a scope the row flags as missing can never actually be granted.
    const reconnected = await reauthorize(props.inbox.id, {
      includeInstagramScopes: Boolean(props.inbox.instagram_id),
    });
    if (reconnected) {
      useAlert(t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_SUCCESS'));
      await fetchPermissions();
    }
  } catch (error) {
    useAlert(t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_ERROR'));
  } finally {
    isReconnecting.value = false;
  }
};

// Warm the SDK so the reconnect click opens its popup within the gesture's
// activation window (see useFacebookPageConnect).
onMounted(preloadSdk);
onMounted(fetchPermissions);
</script>

<template>
  <div class="flex flex-col gap-2 p-4 mb-4 border rounded-lg border-n-weak">
    <label class="text-heading-3 text-n-slate-12">
      {{ $t('INBOX_MGMT.FACEBOOK_CONNECTION.TITLE') }}
    </label>
    <p v-if="grantedScopes === null" class="mb-0 text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.FACEBOOK_CONNECTION.UNKNOWN') }}
    </p>
    <p v-else-if="missingScopes.length" class="mb-0 text-sm text-n-amber-11">
      {{
        $t('INBOX_MGMT.FACEBOOK_CONNECTION.MISSING', {
          permissions: missingPermissionsText,
        })
      }}
    </p>
    <p v-else class="mb-0 text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.FACEBOOK_CONNECTION.CONNECTED') }}
    </p>
    <p class="mb-0 text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.FACEBOOK_CONNECTION.NO_DATA_LOSS') }}
    </p>
    <div class="flex">
      <NextButton
        data-testid="fb-reconnect"
        :label="$t('INBOX_MGMT.FACEBOOK_CONNECTION.RECONNECT')"
        :is-loading="isReconnecting"
        faded
        slate
        sm
        @click="handleReconnect"
      />
    </div>
  </div>
</template>
